import ActionMenu from '@/components/common/ActionMenu';
import { IconButton, MenuButton } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    ArrowRight,
    Settings02,
    Trash01,
    TypeSquare,
} from '@/components/common/icons';
import { IconSVGComponentProps } from '@/components/common/icons/IconComponent';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Text } from '@/components/common/Typography';
import { Center, FlexProps, MenuItem } from '@chakra-ui/react';
import { isSingleplayer, useCurrentUser } from '@common/react/user';
import { GoalAccountOnboardingStep } from '@common/types';
import { Fragment } from 'react';
import { showGoalDeleteModal } from '../../GoalDeleteModal';
import { showGoalRenameModal } from '../../GoalRenameModal';
import { useGoalDetailContext } from '../GoalDetailContext';
import GoalDetailHeaderLayout from '../GoalDetailHeaderLayout';
import { getIncludeTargetStep } from '../helpers';
import GoalOnboardingAccountType from './GoalOnboardingAccountType';
import GoalOnboardingConfirm from './GoalOnboardingConfirm';
import GoalOnboardingConnect from './GoalOnboardingConnect';
import { useGoalOnboardingContext } from './GoalOnboardingContext';
import GoalOnboardingPortfolio from './GoalOnboardingPortfolio';
import GoalOnboardingTarget from './GoalOnboardingTarget';
import GoalOnboardingPlan from './plan/GoalOnboardingPlan';

type OnboardingBreadcrumbStep = BreadcrumbStep & {
    views: GoalAccountOnboardingStep[];
};

const BREADCRUMB_STEPS: Omit<OnboardingBreadcrumbStep, 'onClick'>[] = [
    {
        stepId: 'INVEST',
        label: 'Invest',
        views: ['PORTFOLIO', 'PLAN'],
    },
    {
        stepId: 'CONNECT',
        label: 'Connect',
        views: ['CONNECT'],
    },
    {
        stepId: 'CONFIRM',
        label: 'Confirm',
        views: ['CONFIRM'],
    },
];

export default function GoalOnboarding() {
    const currentUser = useCurrentUser();
    const { setPosition, onClose } = useGoalDetailContext();
    const { goal, step, setStep, updateGoalOnboarding, isUpdating } =
        useGoalOnboardingContext();
    const breadcrumbSteps = useBreadcrumbSteps();

    const includeTargetStep = getIncludeTargetStep(goal.type);

    return (
        <FlexCol overflow="hidden" h="full">
            {step !== 'ACCOUNT_TYPE' && (
                <GoalDetailHeaderLayout>
                    <Breadcrumbs
                        steps={breadcrumbSteps}
                        theme={
                            goal.shareStatus === 'PRIVATE' &&
                            !isSingleplayer(currentUser)
                                ? {
                                      active: { color: 'white' },
                                      disabled: { color: 'whiteAlpha.700' },
                                      default: {
                                          color: 'whiteAlpha.700',
                                          bg: 'transparent',
                                          hover: {
                                              color: 'whiteAlpha.800',
                                              bg: 'whiteAlpha.100',
                                          },
                                      },
                                  }
                                : undefined
                        }
                    />
                    <ActionMenu
                        menuButton={
                            <MenuButton
                                as={IconButton}
                                icon={<Settings02 size="sm" />}
                                ml={1}
                                size="xs"
                                variant="ghost"
                                colorScheme="gray"
                                onClickEvent="Goal Onboarding Settings Menu Button Clicked"
                            />
                        }
                    >
                        <MenuItem
                            icon={<TypeSquare />}
                            onClick={() => showGoalRenameModal({ goal })}
                        >
                            Rename
                        </MenuItem>
                        <MenuItem
                            icon={<Trash01 />}
                            onClick={() => {
                                showGoalDeleteModal({
                                    goal,
                                    onComplete: onClose,
                                });
                            }}
                        >
                            Delete
                        </MenuItem>
                    </ActionMenu>
                </GoalDetailHeaderLayout>
            )}
            {step === 'ACCOUNT_TYPE' ? (
                <GoalOnboardingAccountType
                    goalType={goal.type}
                    initialAccountStructure={goal.accountStructure}
                    goal={goal}
                    updateGoalOnboarding={updateGoalOnboarding}
                    isUpdating={isUpdating}
                    onComplete={() =>
                        includeTargetStep
                            ? setStep('TARGET')
                            : setStep('PORTFOLIO')
                    }
                />
            ) : step === 'TARGET' ? (
                <GoalOnboardingTarget
                    onBack={
                        breadcrumbSteps[0].stepId === 'GOAL_TYPE'
                            ? () => setStep('ACCOUNT_TYPE')
                            : undefined
                    }
                    onComplete={() => setStep('PORTFOLIO')}
                />
            ) : step === 'PORTFOLIO' ? (
                <GoalOnboardingPortfolio
                    onBack={
                        breadcrumbSteps[0].stepId === 'GOAL_TYPE'
                            ? () =>
                                  includeTargetStep
                                      ? setStep('TARGET')
                                      : setStep('ACCOUNT_TYPE')
                            : undefined
                    }
                    onComplete={() => setStep('PLAN')}
                />
            ) : step === 'PLAN' ? (
                <GoalOnboardingPlan
                    onBack={() => setStep('PORTFOLIO')}
                    onComplete={(step) => setStep(step ?? 'CONNECT')}
                />
            ) : step === 'CONNECT' ? (
                <GoalOnboardingConnect
                    onBack={() => setStep('PLAN')}
                    onComplete={() => setStep('CONFIRM')}
                />
            ) : step === 'CONFIRM' ? (
                <GoalOnboardingConfirm
                    onBack={() => setStep('CONNECT')}
                    onComplete={() => setPosition('CELEBRATION')}
                />
            ) : (
                <></>
            )}
        </FlexCol>
    );
}

const useBreadcrumbSteps = () => {
    const { goalOnboardingData, step, setStep, goal } =
        useGoalOnboardingContext();
    const { lastCompletedStep } = goalOnboardingData;

    // Construct breadcrumb steps
    let steps: Omit<OnboardingBreadcrumbStep, 'onClick'>[] = [
        ...BREADCRUMB_STEPS,
    ];
    // Only the original creator of the goal can set ownership/account type.
    if (goal.isMine) {
        steps = [
            {
                stepId: 'GOAL_TYPE',
                label: 'Goal type',
                views: ['ACCOUNT_TYPE', 'TARGET'],
            },
            ...steps,
        ];
    }

    const maxIndex = lastCompletedStep
        ? steps.findIndex((s) => s.views.at(-1) === lastCompletedStep) + 1 // A step is only considered complete if the last completed step is the _last_ view of the step
        : 1;
    const activeBreadcrumbIndex = steps.findIndex((s) =>
        s.views.includes(step)
    );

    const breadcrumbSteps: OnboardingBreadcrumbStep[] = steps.map((step, i) => {
        const isDisabled = i > maxIndex;
        return {
            ...step,
            isActive: i === activeBreadcrumbIndex,
            isDisabled,
            onClick: (stepId) => {
                if (isDisabled) return;
                const newStep = steps
                    .find((step) => step.stepId === stepId)
                    ?.views.at(0);
                if (newStep) {
                    setStep(newStep);
                }
            },
        };
    });

    return breadcrumbSteps;
};

type BreadcrumbStep<
    Id extends string = 'GOAL_TYPE' | 'INVEST' | 'CONNECT' | 'CONFIRM',
> = {
    stepId: Id;
    label: string;
    isActive?: boolean;
    isDisabled?: boolean;
    onClick: (stepId: Id) => void;
};
interface BreadcrumbProps extends FlexProps {
    steps: BreadcrumbStep[];
    theme?: {
        active?: {
            color?: IconSVGComponentProps['color'];
            bg?: IconSVGComponentProps['color'];
        };
        disabled?: {
            color?: IconSVGComponentProps['color'];
            bg?: IconSVGComponentProps['color'];
        };
        default?: {
            color?: IconSVGComponentProps['color'];
            bg?: IconSVGComponentProps['color'];
            hover: {
                color?: IconSVGComponentProps['color'];
                bg?: IconSVGComponentProps['color'];
            };
        };
    };
}
function Breadcrumbs({ steps, theme, ...props }: BreadcrumbProps) {
    return (
        <FlexRow gap={4} align="center" {...props}>
            {steps.map((step, i) => {
                const activeColor = theme?.active?.color ?? 'white';
                const activeBg = theme?.active?.bg ?? 'blue.500';
                const disabledColor = theme?.disabled?.color ?? 'textMuted';
                const disabledBg = theme?.disabled?.bg ?? 'transparent';
                const defaultColor = theme?.default?.color ?? 'textMuted';
                const defaultBg = theme?.default?.bg ?? 'transparent';
                const defaultHoverColor =
                    theme?.default?.hover?.color ?? 'textMuted';
                const defaultHoverBg = theme?.default?.hover?.bg ?? 'gray.100';

                const color = step.isActive
                    ? activeColor
                    : step.isDisabled
                      ? disabledColor
                      : defaultColor;
                const bg = step.isActive
                    ? activeBg
                    : step.isDisabled
                      ? disabledBg
                      : defaultBg;
                const hoverStyles = step.isDisabled
                    ? {}
                    : step.isActive
                      ? hoverStyleProps({})
                      : hoverStyleProps({
                            color: defaultHoverColor,
                            bg: defaultHoverBg,
                        });

                return (
                    <Fragment key={step.stepId}>
                        {i !== 0 && (
                            <ArrowRight
                                key={'arrow' + step.stepId}
                                color={defaultColor}
                                size="xs"
                            />
                        )}
                        <Center
                            {...hoverStyles}
                            bg={bg}
                            color={color}
                            px={3}
                            h={6}
                            rounded="full"
                            onClick={() => {
                                !step.isDisabled && step.onClick(step.stepId);
                            }}
                        >
                            <Text fontSize="sm" fontWeight="medium">
                                {step.label}
                            </Text>
                        </Center>
                    </Fragment>
                );
            })}
        </FlexRow>
    );
}
